import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import "./style.css";

/* ── 3D Tilt card wrapper (matching udghosh TiltCard) ── */
const TiltCard = ({ children }) => {
  const ref  = useRef(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotX = useSpring(useTransform(rawY, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const rotY = useSpring(useTransform(rawX, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top)  / rect.height - 0.5);
  };

  const onLeave = () => { rawX.set(0); rawY.set(0); };

  return (
    <motion.div
      ref={ref}
      className="tilt-wrapper"
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
};

/* ── Team contacts ── */
const contacts = [
  {
    name:        "Abhishek",
    role:        "Head, Hospitality",
    img:         "https://live.staticflickr.com/65535/55426699563_f4d0e1fee3_b.jpg",
    imgPosition: "bottom center",
    phone:       "tel:+919927860926",
    linkedin:    "",
    email:       "mailto:sabhishekk23@iitk.ac.in",
  },
  {
    name:        "Aayush Dubey",
    role:        "Head, Hospitality",
    img:         "https://live.staticflickr.com/65535/55426460391_a528dde623_b.jpg",
    imgPosition: "bottom center",
    phone:       "tel:+917073334221",
    linkedin:    "",
    email:       "mailto:aayushd23@iitk.ac.in",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18 } },
};

const cardVariants = {
  hidden:  { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const ProfileCards = () => (
  <section className="contact-section" id="Contacts">
    {/* Header */}
    <div className="udg-section-header contact-header">
      <motion.span
        className="udg-eyebrow"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        Reach Out
      </motion.span>
      <motion.h2
        className="udg-section-title font-display"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        Need Help? We Got You!
      </motion.h2>
      <motion.span
        className="udg-gold-rule"
        style={{ margin: "0 auto", transformOrigin: "left" }}
        initial={{ opacity: 0, scaleX: 0 }}
        whileInView={{ opacity: 1, scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2, duration: 0.6 }}
      />
    </div>

    {/* Cards grid */}
    <motion.div
      className="contact-cards"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
    >
      {contacts.map((c) => (
        <motion.div key={c.name} variants={cardVariants}>
          <TiltCard>
            <div className="profile-card udg-card">
              {/* Photo */}
              <div className="profile-photo-ring">
                <img
                  src={c.img}
                  alt={c.name}
                  className="profile-photo"
                  style={c.imgPosition ? { objectPosition: c.imgPosition } : undefined}
                />
              </div>

              {/* Info */}
              <p className="profile-name">{c.name}</p>
              <p className="profile-role">{c.role}</p>

              {/* Links */}
              <ul className="profile-links">
                <li>
                  <a href={c.phone} target="_blank" rel="noreferrer" aria-label={`Call ${c.name}`} className="profile-link-icon">
                    {/* Phone icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.5a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                {c.linkedin && (
                  <li>
                    <a href={c.linkedin} target="_blank" rel="noreferrer" aria-label={`LinkedIn — ${c.name}`} className="profile-link-icon">
                      {/* LinkedIn icon */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
                        <circle cx="4" cy="4" r="2"/>
                      </svg>
                    </a>
                  </li>
                )}
                <li>
                  <a href={c.email} target="_blank" rel="noreferrer" aria-label={`Email ${c.name}`} className="profile-link-icon">
                    {/* Email icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22,6 12,13 2,6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </div>
          </TiltCard>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

export default ProfileCards;
